import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { resolveLocalizedName } from 'pi-kiosk-shared';
import { resolveRetailHandoffChoices } from '../../../checkout/components/collectHandoffChoices.js';
import { resolvePickupCollectTitleKey } from '../../../checkout/components/collectSoleOptionPresentation.js';
import { shouldShowNowTimingUnderLaterLock } from '../../../shop/presenters/shopCheckoutPresenter.js';
import { JourneyShell } from '../../JourneyShell.js';
import type { UsePhoneFirstCheckoutScreenResult } from '../../hooks/usePhoneFirstCheckoutScreen.js';
import {
  usePublishHierarchicalBackFlow,
  type HierarchicalBackFlowPublishInput,
} from '../../../../shared/context/HierarchicalBackFlowContext.js';
import { CUSTOMER_CONTENT_NARROW_CLASS } from '../../../../app/customerTopBarStyles.js';
import { PhoneFirstCheckoutMissingSessionView } from './PhoneFirstCheckoutMissingSessionView.js';
import { PhoneFirstCheckoutQrStepView } from './PhoneFirstCheckoutQrStepView.js';
import { PhoneFirstCheckoutCollectStepView } from './PhoneFirstCheckoutCollectStepView.js';
import { PhoneFirstCheckoutCashCollectStepView } from './PhoneFirstCheckoutCashCollectStepView.js';
import { PhoneFirstCheckoutPaymentStepView } from './PhoneFirstCheckoutPaymentStepView.js';

export type PhoneFirstCheckoutScreenViewProps = UsePhoneFirstCheckoutScreenResult;

export function PhoneFirstCheckoutScreenView(props: PhoneFirstCheckoutScreenViewProps): JSX.Element {
  const { i18n } = useTranslation('customer');
  const {
    t,
    activeStep,
    buildShopRecoveryPath,
    setCheckoutStep,
    paymentRailUiStep,
    popCheckoutStepBack,
    resolveCheckoutStepBackTo,
    isDonationSession,
  } = props;
  const shopExit = buildShopRecoveryPath();

  const paymentActive = activeStep === 'payment' || activeStep === 'qr';
  const stepBackTo = resolveCheckoutStepBackTo();

  /**
   * Same-URL pops only — cross-route exits rely on stepBackTo / shell backTo.
   * Publish exclusivity: canPopInPlace → onStepBack only (null stepBackTo);
   * else → stepBackTo only (null onStepBack). JourneyShell content onBack/backTo
   * mirrors the same parents for non-shell / shellOwnsBack=false; W2 suppress
   * hides content ← when fixed shell owns step or link back.
   */
  const canPopInPlace =
    activeStep === 'qr' ||
    (activeStep === 'payment' && paymentRailUiStep === 'gateway-methods') ||
    (activeStep === 'payment' && !isDonationSession);

  const popCheckoutStepBackRef = useRef(popCheckoutStepBack);
  useEffect(() => {
    popCheckoutStepBackRef.current = popCheckoutStepBack;
  }, [popCheckoutStepBack]);

  // Match JourneyShell payment `backDisabled`; only lock same-URL onStepBack publishes.
  // Cross-route (stepBackTo only) leaves backDisabled false.
  const paymentBackDisabled =
    props.payActionsDisabled === true && paymentRailUiStep !== 'gateway-methods';
  const publishBackDisabled = canPopInPlace ? paymentBackDisabled : false;

  const publishInput = useMemo((): HierarchicalBackFlowPublishInput => {
    return {
      flowKind: 'checkout',
      flowStep: activeStep,
      paymentRailUiStep: activeStep === 'payment' ? paymentRailUiStep : null,
      paymentActive,
      // Never publish stepBackTo when same-URL onStepBack is active.
      stepBackTo: canPopInPlace ? null : stepBackTo,
      onStepBack: canPopInPlace
        ? (): void => {
            popCheckoutStepBackRef.current();
          }
        : null,
      backDisabled: publishBackDisabled,
    };
  }, [
    activeStep,
    canPopInPlace,
    paymentActive,
    paymentRailUiStep,
    publishBackDisabled,
    stepBackTo,
  ]);

  // Single system-back writer — HierarchicalBackFlow.publishFlow via helper.
  usePublishHierarchicalBackFlow(publishInput);

  if (activeStep === 'missing-session') {
    return (
      <JourneyShell
        title={t('checkout.productPaymentTitle')}
        subtitle={t('checkout.requiresCartSubtitle')}
        context="PHONE_FIRST_PRODUCT"
        backTo={shopExit}
      >
        <div className={CUSTOMER_CONTENT_NARROW_CLASS}>
        <PhoneFirstCheckoutMissingSessionView
          tenantCode={props.tenantCode}
          checkoutHelpOpen={props.checkoutHelpOpen}
          setCheckoutHelpOpen={props.setCheckoutHelpOpen}
          locationPathAndSearch={props.locationPathAndSearch}
          navigate={props.navigate}
        />
        </div>
      </JourneyShell>
    );
  }

  if (activeStep === 'cash-collect' && props.cashCollectShellResult !== null) {
    return (
      <JourneyShell
        title={
          props.cashPaymentConfirmed
            ? t('checkout.cashCollect.paidTitle')
            : t('checkout.cashCollect.waitingTitle')
        }
        subtitle={
          props.checkoutSession !== null
            ? `${(props.subtitleAmountMinor / 100).toFixed(2)} ${props.subtitleCurrency}`
            : t('checkout.loading')
        }
        context="PHONE_FIRST_PRODUCT"
        backTo={shopExit}
        backLabel={t('checkout.success.backToShop')}
      >
        <div className={CUSTOMER_CONTENT_NARROW_CLASS}>
        <PhoneFirstCheckoutCashCollectStepView
          t={t}
          cashCompleteResult={props.cashCollectShellResult}
          orderNumber={props.cashCollectOrderNumber}
          kioskName={
            props.kioskInfo.data?.name !== undefined
              ? resolveLocalizedName(
                  props.kioskInfo.data.name,
                  props.kioskInfo.data.nameLocales,
                  i18n.language,
                ).trim() || t('checkout.cashCollect.kioskFallback')
              : t('checkout.cashCollect.kioskFallback')
          }
          tenantDisplayName={props.tenantDisplayName}
          cashPaymentConfirmed={props.cashPaymentConfirmed}
          allowContinueWithoutPaid={props.allowCashContinueWithoutPaid}
          onContinue={props.continueAfterPay}
        />
        </div>
      </JourneyShell>
    );
  }

  if (activeStep === 'qr' && props.activeQrPayment !== null && props.sessionIdFromUrl !== null) {
    return (
      <JourneyShell
        title={t('checkout.paymentTitle')}
        subtitle={t('checkout.qrSubtitle')}
        context="PHONE_FIRST_PRODUCT"
        backTo={null}
        onBack={() => {
          if (!popCheckoutStepBack()) {
            void props.handleExitPaymentFlow();
          }
        }}
      >
        <div className={CUSTOMER_CONTENT_NARROW_CLASS}>
        <PhoneFirstCheckoutQrStepView
          activeQrPayment={props.activeQrPayment}
          sessionIdFromUrl={props.sessionIdFromUrl}
          checkoutSession={props.checkoutSession}
          onCancel={() => {
            if (!popCheckoutStepBack()) {
              void props.handleExitPaymentFlow();
            }
          }}
          onContinue={props.continueAfterPay}
        />
        </div>
      </JourneyShell>
    );
  }

  if (activeStep === 'collect') {
    // Match CollectPickupSheetRetail timing visibility so JourneyShell title
    // does not contradict sole announce (e.g. sole SELF_SERVICE ASAP → titleSole).
    const preserveUsableNow = shouldShowNowTimingUnderLaterLock(
      props.lockedCollectTiming,
      props.effectiveCollectSelection,
    );
    const showNowTiming =
      props.lockedCollectTiming === undefined ||
      props.lockedCollectTiming === 'NOW' ||
      preserveUsableNow;
    const showLaterTiming =
      !preserveUsableNow &&
      (props.lockedCollectTiming === undefined || props.lockedCollectTiming === 'LATER') &&
      props.scheduledPickupEnabled;
    const soleInput = {
      handoffChoiceCount: resolveRetailHandoffChoices(props.allowedPickupHandoffModes).length,
      showNowTiming,
      showLaterTiming,
      pickupPointCount: props.pickupPoints.length,
    };
    return (
      <JourneyShell
        title={t(resolvePickupCollectTitleKey(soleInput))}
        subtitle={
          props.checkoutSession !== null
            ? `${(props.subtitleAmountMinor / 100).toFixed(2)} ${props.subtitleCurrency}`
            : t('checkout.loading')
        }
        context="PHONE_FIRST_PRODUCT"
        backTo={shopExit}
        backLabel={t('checkout.success.backToShop')}
      >
        <div className={CUSTOMER_CONTENT_NARROW_CLASS}>
        <PhoneFirstCheckoutCollectStepView {...props} />
        </div>
      </JourneyShell>
    );
  }

  let paymentBackTo: string | null = null;
  let paymentOnBack: (() => void) | undefined;
  let paymentBackLabel: string | undefined;
  if (paymentRailUiStep === 'gateway-methods') {
    paymentOnBack = () => {
      if (!popCheckoutStepBack()) {
        props.setPaymentRailUiStep('primary');
        props.setGatewayMethodOptions([]);
        props.setPaymentError(null);
      }
    };
    paymentBackLabel = t('payment.gatewayMethods.back');
  } else if (isDonationSession) {
    paymentBackTo = shopExit;
    paymentBackLabel = t('checkout.success.backToShop');
  } else {
    paymentOnBack = () => {
      if (!popCheckoutStepBack()) {
        setCheckoutStep('collect');
      }
    };
    paymentBackLabel = t('pickup.backToCollect');
  }

  return (
    <JourneyShell
      title={
        paymentRailUiStep === 'gateway-methods'
          ? t('payment.gatewayMethods.title')
          : t('checkout.selectMethodTitle')
      }
      subtitle={
        props.checkoutSession !== null
          ? `${(props.subtitleAmountMinor / 100).toFixed(2)} ${props.subtitleCurrency}`
          : t('checkout.loading')
      }
      context="PHONE_FIRST_PRODUCT"
      backTo={paymentBackTo}
      onBack={paymentOnBack}
      backLabel={paymentBackLabel}
      backDisabled={paymentBackDisabled}
    >
      <div className={CUSTOMER_CONTENT_NARROW_CLASS}>
      <PhoneFirstCheckoutPaymentStepView {...props} />
      </div>
    </JourneyShell>
  );
}
